import { BaseDatasetDropzone } from "../BaseDatasetDropzone/BaseDatasetDropzone";
import posthogEvents from "@app/config/posthog/posthog-events";
import { useCreateExperimentDraft, useExperimentListWithSorting } from "@app/queries/experiment";
import { useFavorites, useRecents } from "@app/queries/iam";
import type { Experiment, ExperimentId } from "@globals";
import Icon from "@lib/common/components/Icon/Icon";
import TimeAgo from "@lib/common/components/TimeAgo/TimeAgo";
import ExperimentIcon from "@lib/experiment/ExperimentIcon/ExperimentIcon";
import ExperimentStatusBadge from "@lib/experiment/ExperimentStatusBadge/ExperimentStatusBadge";
import formatExperimentName from "@lib/experiment/utils/formatExperimentName";
import resolveExperimentLinkTo from "@lib/experiment/utils/resolveExperimentLinkTo";
import { Group, VisuallyHidden } from "@mantine/core";
import { useHotkeys } from "@mantine/hooks";
import type { SpotlightActionData, SpotlightActionGroupData, SpotlightFilterFunction } from "@mantine/spotlight";
import { Spotlight, spotlight } from "@mantine/spotlight";
import Fuse from "fuse.js";
import { sortBy, uniq } from "lodash-es";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

export default function SpotlightSearch() {
  const [spotlightActions, setSpotlightActions] = useState<Array<SpotlightActionGroupData | SpotlightActionData>>([]);
  const navigate = useNavigate();
  const posthog = usePostHog();
  useHotkeys(
    [
      [
        "mod+K",
        () => {
          spotlight.toggle();
          posthog.capture(posthogEvents.spotlight_search.toggle_by_hotkey);
        }
      ]
    ],
    []
  );
  const dropzoneRef = useRef<() => void>(null);
  const createExperimentDraft = useCreateExperimentDraft();

  const createAndNavigateToDraft = useCallback(async () => {
    const draft = await createExperimentDraft.mutateAsync({});
    await navigate(`/experiments/drafts/${draft.id}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const favoriteExperimentsQuery = useFavorites();
  const recentExperimentsQuery = useRecents();
  const experimentsQuery = useExperimentListWithSorting({
    limit: 20,
    offset: 0,
    sort_field: "updated_at",
    sort_order: "desc",
    created_by: undefined,
    exclude_drafts: false
  });

  const shortcutActions: SpotlightActionData[] = useMemo(
    () => [
      {
        id: "home",
        label: "Home",
        leftSection: <Icon name="home" color="dimmed" />,
        rightSection: <Icon name="arrow-right" color="dimmed" size="xs" />,
        onClick: () => {
          posthog.capture(posthogEvents.spotlight_search.home_click);
          void navigate(`/`);
        }
      },
      {
        id: "new-experiment",
        label: "New experiment",
        leftSection: <Icon name="plus" color="dimmed" />,
        rightSection: <Icon name="arrow-right" color="dimmed" size="xs" />,
        onClick: () => {
          posthog.capture(posthogEvents.spotlight_search.new_experiment_click);
          void createAndNavigateToDraft();
        }
      },
      {
        id: "upload-dataset",
        label: "Upload dataset",
        leftSection: <Icon name="upload" color="dimmed" />,
        rightSection: <Icon name="arrow-right" color="dimmed" size="xs" />,
        onClick: () => {
          posthog.capture(posthogEvents.spotlight_search.upload_dataset_click);
          dropzoneRef.current?.();
        }
      }
    ],
    [navigate, createAndNavigateToDraft, posthog]
  );

  function experimentActionData(
    experiment: Pick<Experiment, "id" | "name" | "status" | "updated_at" | "is_favorite">,
    posthogEvent: keyof (typeof posthogEvents)["spotlight_search"]
  ): SpotlightActionData {
    const { id, name, status, updated_at, is_favorite } = experiment;
    return {
      id,
      label: formatExperimentName(name),
      leftSection: <ExperimentIcon status={status} size="xs" />,
      rightSection: (
        <Group gap="sm" wrap="nowrap">
          {is_favorite && <Icon name="star" size="xs" color="yellow" variant="solid" />}
          <ExperimentStatusBadge status={status} size="xs" />
          <TimeAgo date={new Date(updated_at)} size="xs" c="dimmed" lineClamp={1} lh={1} />
        </Group>
      ),
      onClick: () => {
        posthog.capture(posthogEvents.spotlight_search[posthogEvent]);
        void navigate(resolveExperimentLinkTo(id, status));
      }
    };
  }

  useEffect(() => {
    const actions: Array<SpotlightActionGroupData | SpotlightActionData> = [...shortcutActions];

    if (favoriteExperimentsQuery.data && recentExperimentsQuery.data && experimentsQuery.data.results) {
      const favorites = sortBy(favoriteExperimentsQuery.data.experiments, "updated_at")
        .reverse()
        .map(experiment => experimentActionData(experiment, "favorite_experiment_click"));

      // Filter out Favorites from Recent.
      const recent = sortBy(
        recentExperimentsQuery.data.experiments.filter(({ id }) => !favorites.find(exp => exp.id === id)),
        "updated_at"
      )
        .reverse()
        .map(experiment => experimentActionData(experiment, "recent_experiment_click"));

      // All other experiments not Favorite or Recent.
      const excludeFromRest: Array<ExperimentId> = uniq([
        ...favorites.map(exp => exp.id),
        ...recent.map(exp => exp.id)
      ]);
      const rest = sortBy(
        experimentsQuery.data.results.filter(exp => !excludeFromRest.includes(exp.id)),
        "updated_at"
      )
        .reverse()
        .map(experiment => experimentActionData(experiment, "other_experiment_click"));

      if (favorites.length > 0) {
        actions.push({
          group: "Favorites",
          actions: favorites
        });
      }
      if (recent.length > 0) {
        actions.push({
          group: "Recent",
          actions: recent
        });
      }
      if (rest.length > 0) {
        actions.push({
          group: "Other experiments",
          actions: rest
        });
      }
    }

    setSpotlightActions(actions);
  }, [
    setSpotlightActions,
    navigate,
    experimentsQuery.data,
    favoriteExperimentsQuery.data,
    recentExperimentsQuery.data,
    posthog,
    shortcutActions
  ]);

  const fuzzySearchFilter: SpotlightFilterFunction = (query, searchActions) => {
    if (!query.trim()) {
      return searchActions;
    }

    const flatActions = searchActions.reduce<SpotlightActionData[]>((acc, item) => {
      if ("actions" in item) {
        return [...acc, ...item.actions.map(action => ({ ...action, group: item.group }))];
      }
      return [...acc, item];
    }, []);

    const fuse = new Fuse(flatActions, {
      keys: ["label", "description"],
      threshold: 0.5,
      minMatchCharLength: 1
    });

    const results = fuse.search(query).map(result => result.item);
    const groups: Record<string, { pushed: boolean; data: { group: string; actions: SpotlightActionData[] } }> = {};
    const result: Array<SpotlightActionGroupData | SpotlightActionData> = [];

    // Maintain group structure.
    results.forEach(action => {
      if (action.group) {
        if (!groups[action.group]) {
          groups[action.group] = { pushed: false, data: { group: action.group, actions: [] } };
        }
        groups[action.group].data.actions.push(action);
        if (!groups[action.group].pushed) {
          groups[action.group].pushed = true;
          result.push(groups[action.group].data);
        }
      } else {
        result.push(action);
      }
    });

    // Mantine Spotlight unmounts its actions list when filtered results are empty,
    // resetting its internal `listId` to "". Pressing Enter then builds the invalid
    // selector `# [data-selected]` and throws SyntaxError. Returning an inert stub
    // keeps the list mounted so Enter is a safe no-op.
    if (result.length === 0) {
      result.push({
        id: "spotlight-no-results",
        label: "Nothing found",
        onClick: () => {}
      });
    }

    return result;
  };

  return (
    <>
      <Spotlight
        actions={spotlightActions}
        filter={fuzzySearchFilter}
        highlightQuery
        closeOnClickOutside
        scrollable
        limit={30}
        maxHeight="80vh"
        returnFocus={false}
        searchProps={{
          leftSection: <Icon name="magnifying-glass" />,
          placeholder: "Find experiments"
        }}
        scrollAreaProps={{ type: "always" }}
        shortcut={[]}
        size="xl"
      />
      <VisuallyHidden>
        <BaseDatasetDropzone p={0} openRef={dropzoneRef} />
      </VisuallyHidden>
    </>
  );
}
